<template>
  <div
    v-if="isVisible"
    ref="container"
    :class="['recommendation-item', { 'is-selected': props.selected }]"
    :style="containerStyle"
  >
    <Stack
      gap="1"
      class="recommendation-body"
    >
      <header class="recommendation-header">
        <div class="recommendation-title">
          <Title size="4_5">
            {{ item.headline }}
          </Title>
          <!-- <RecommendationCategory category="estate" /> -->
        </div>

        <Badge
          v-if="item.entity_name"
          class="recommendation-entity"
          status="info"
          icon-before="fas fa-user"
        >
          {{ item.entity_name }}
        </Badge>
      </header>

      <div class="recommendation-description">
        {{ item.detail }}
      </div>

      <div class="recommendation-action">
        {{ item.action }}
      </div>
    </Stack>

    <div class="recommendation-options">
      <Buttons>
        <Button
          v-for="button in buttons"
          :key="button.label"
          :icon-before="button.icon"
          :shade="button.shade"
          :disabled="taskStore.tasks.isSavingTask"
          spacing="tight"
          @click="handleAction(button.status)"
        >
          {{ button.label }}
        </Button>
      </Buttons>
    </div>
  </div>
</template>

<script setup>
import { defineEmits, ref, watch, computed, nextTick } from 'vue';
import { Badge, Buttons, Button, Stack, Title } from '@stakeui';

import { useTaskStore } from '@frontend/ui/stores/taskStore.js';
const taskStore = useTaskStore();

const props = defineProps({
  item: { type: Object, required: true },
  selected: { type: Boolean, default: false },
  hide: { type: Boolean, default: false },
  duration: { type: Number, default: 300 },
});
const emit = defineEmits(['hidden']);

const container = ref(null);
const height = ref('auto');
const padding = ref('1em');
const borderWidth = ref('var(--stk-control-border-width)');
const outlineWidth = ref('1px');
const isVisible = ref(!props.hide);

const processedItems = computed(() => taskStore.recommendations.processedItems || []);

const buttons = computed(() => {
  return [
    // Turning this off for simplicity, since tasks can be assigned in edit mode.
    // Can implement later if needed
    /* {
      id: 'assign',
      label: 'Assign',
      icon: 'fas fa-arrow-circle-right',
      shade: 'info',
      letter: 'a',
    }, */
    {
      id: 'snooze',
      status: 'snoozed',
      label: 'Snooze',
      icon: 'fas fa-hourglass-half',
      shade: 'warning',
      letter: 's',
    },
    {
      id: 'dismiss',
      status: 'dismissed',
      label: 'Dismiss',
      icon: 'fas fa-ban',
      shade: 'danger',
      letter: 'd',
    },
    {
      id: 'accept',
      status: 'accepted',
      label: 'Accept',
      icon: 'fas fa-check',
      shade: 'success',
      letter: 'f',
    },
  ];
});

const containerStyle = computed(() => ({
  height: height.value,
  padding: padding.value,
  borderWidth: borderWidth.value,
  outlineWidth: outlineWidth.value,
  opacity: props.hide ? 0 : 1,
  overflow: 'hidden',
  transition: `height ${props.duration}ms ease, padding ${props.duration}ms ease, border-width ${props.duration}ms ease, outline-width ${props.duration}ms ease, opacity ${props.duration}ms ease`,
}));

function collapse() {
  if (!container.value) {
    return;
  }

  height.value = container.value.scrollHeight + 'px';
  padding.value = window.getComputedStyle(container.value).padding;
  borderWidth.value = window.getComputedStyle(container.value).borderWidth;
  outlineWidth.value = window.getComputedStyle(container.value).outlineWidth;

  nextTick(() => {
    void container.value.offsetHeight; // Force reflow
    height.value = '0';
    padding.value = '0';
    borderWidth.value = '0';
    outlineWidth.value = '0';
    setTimeout(() => {
      isVisible.value = false;
      emit('hidden', props.item.id);
    }, props.duration);
  });
}

const handleAction = action => {
  // Right now snooze is hardcoded to tomorrow at 9am
  // TODO: Allow user to choose snooze duration
  const snooze = new Date();
  snooze.setDate(snooze.getDate() + 1);
  snooze.setHours(9, 0, 0, 0);

  taskStore.saveTask({
    ...props.item,
    status: action,
    name: props.item.headline,
    description: props.item.action,
    snooze: action === 'snoozed' ? snooze : null,
  });
};

// Collapse when this item is marked processed
watch(
    processedItems,
    (newVal, oldVal) => {
      if (newVal && newVal.includes(props.item.id)) {
        collapse();
      }
    },
    { deep: true, immediate: true },
);
</script>

<style scoped>
.recommendation-item {
  background-color: var(--stk-control-background-color);
  border-color: var(--stk-control-border-color);
  border-radius: 0.75em;
  border-style: solid;
  border-width: var(--stk-control-border-width);
  box-shadow: var(--stk-control-shadow);
  padding: 1em;
  margin-bottom: 1em;
  overflow: hidden;
  position: relative;
  transition: height 200ms ease, opacity 200ms ease;

  &.is-selected {
    border-color: var(--stk-primary);
    outline-color: var(--stk-primary);
    outline-width: 1px;
    outline-style: solid;
    box-shadow: 0px 4px 8px 0px rgba(0, 0, 0, 0.20), 0px 2px 2px 0px rgba(0, 0, 0, 0.02), 0px 1px 1px 0px rgba(0, 0, 0, 0.02);
  }

  &:hover {
    .recommendation-options {
      opacity: 1;
    }
  }
}

.recommendation-body {
  position: relative;
}

.recommendation-options {
  background-image: linear-gradient(45deg, transparent 70%, rgb(0 0 0 / 50%));
  position: absolute;
  inset: 0;
  display: flex;
  align-items: start;
  justify-content: end;
  padding: 1em;
  opacity: 0;
}

.recommendation-header {
  align-items: center;
  display: flex;
  gap: 1em;
}

.recommendation-title {
  align-items: center;
  display: flex;
  flex-wrap: wrap;
  gap: 1em;
  flex-grow: 1;
}

.recommendation-description {
  color: var(--stk-text-weak);
  font-size: 0.875em;
}

.recommendation-action {
  background-color: var(--stk-primary-95);
  color: var(--stk-primary-50);
  font-size: 0.875em;
  padding: 0.5em 0.75em;
  border-radius: 0.75em;
  font-weight: 500;
}
</style>
